/**
 * NumberInput Component
 * Number input with increment/decrement controls
 */

import React, { forwardRef, useState, createContext, useContext } from 'react';
import { Box } from '../primitives/Box';
import { Input } from './Input';
import { useTheme } from '../theme';

const NumberInputContext = createContext({});

/**
 * NumberInput Component
 * 
 * @example
 * <NumberInput min={0} max={100} step={1} defaultValue={50}>
 *   <NumberInputField />
 *   <NumberInputStepper>
 *     <NumberIncrementStepper />
 *     <NumberDecrementStepper />
 *   </NumberInputStepper>
 * </NumberInput>
 */
export const NumberInput = forwardRef(({
  min = Number.MIN_SAFE_INTEGER,
  max = Number.MAX_SAFE_INTEGER,
  step = 1,
  precision,
  value,
  defaultValue = 0,
  onChange,
  onBlur,
  onFocus,
  isDisabled = false,
  isReadOnly = false,
  isRequired = false,
  isInvalid = false,
  variant = 'outline',
  size = 'md',
  clampValueOnBlur = true,
  allowMouseWheel = false,
  children,
  ...props
}, ref) => {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const currentValue = value !== undefined ? value : internalValue;

  const clamp = (val) => Math.min(max, Math.max(min, val));

  const handleChange = (newValue) => {
    const parsed = parseFloat(newValue);
    if (!isNaN(parsed)) {
      const clamped = clampValueOnBlur ? clamp(parsed) : parsed;
      if (value === undefined) {
        setInternalValue(clamped);
      }
      onChange?.(clamped.toString(), clamped);
    } else if (newValue === '' || newValue === '-') {
      if (value === undefined) {
        setInternalValue(newValue);
      }
      onChange?.(newValue, NaN);
    }
  };

  const increment = () => {
    if (isDisabled || isReadOnly) return;
    const next = (parseFloat(currentValue) || 0) + step;
    handleChange(clamp(next).toString());
  };

  const decrement = () => {
    if (isDisabled || isReadOnly) return;
    const next = (parseFloat(currentValue) || 0) - step;
    handleChange(clamp(next).toString());
  };

  const context = {
    value: currentValue,
    onChange: handleChange,
    increment,
    decrement,
    isDisabled,
    isReadOnly,
    isRequired,
    isInvalid,
    min,
    max,
    step,
    variant,
    size,
  };

  return (
    <NumberInputContext.Provider value={context}>
      <Box ref={ref} position="relative" {...props}>
        {children}
      </Box>
    </NumberInputContext.Provider>
  );
});

NumberInput.displayName = 'NumberInput';

/**
 * NumberInputField Component
 * The input field for NumberInput
 */
export const NumberInputField = forwardRef((props, ref) => {
  const ctx = useContext(NumberInputContext);

  return (
    <Input
      ref={ref}
      type="text"
      inputMode="decimal"
      role="spinbutton"
      aria-valuemin={ctx.min}
      aria-valuemax={ctx.max}
      aria-valuenow={parseFloat(ctx.value) || undefined}
      value={ctx.value}
      onChange={(e) => ctx.onChange(e.target.value)}
      isDisabled={ctx.isDisabled}
      isReadOnly={ctx.isReadOnly}
      isRequired={ctx.isRequired}
      isInvalid={ctx.isInvalid}
      variant={ctx.variant}
      size={ctx.size}
      {...props}
    />
  );
});

NumberInputField.displayName = 'NumberInputField';

/**
 * NumberInputStepper Component
 * Container for stepper buttons
 */
export const NumberInputStepper = forwardRef(({ children, ...props }, ref) => {
  return (
    <Box
      ref={ref}
      position="absolute"
      top="0"
      right="0"
      display="flex"
      flexDirection="column"
      height="100%"
      width="24px"
      borderLeft="1px solid"
      borderColor="gray.200"
      {...props}
    >
      {children}
    </Box>
  );
});

NumberInputStepper.displayName = 'NumberInputStepper';

/**
 * NumberIncrementStepper Component
 */
export const NumberIncrementStepper = forwardRef((props, ref) => {
  const ctx = useContext(NumberInputContext);

  return (
    <Box
      ref={ref}
      as="button"
      type="button"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flex="1"
      cursor={ctx.isDisabled ? 'not-allowed' : 'pointer'}
      opacity={ctx.isDisabled ? 0.4 : 1}
      borderBottom="1px solid"
      borderColor="gray.200"
      bg="transparent"
      _hover={{ bg: 'gray.50' }}
      onClick={ctx.increment}
      disabled={ctx.isDisabled || ctx.isReadOnly}
      {...props}
    >
      <svg viewBox="0 0 24 24" width="0.6em" height="0.6em" fill="currentColor">
        <path d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z" />
      </svg>
    </Box>
  );
});

NumberIncrementStepper.displayName = 'NumberIncrementStepper';

/**
 * NumberDecrementStepper Component
 */
export const NumberDecrementStepper = forwardRef((props, ref) => {
  const ctx = useContext(NumberInputContext);

  return (
    <Box
      ref={ref}
      as="button"
      type="button"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flex="1"
      cursor={ctx.isDisabled ? 'not-allowed' : 'pointer'}
      opacity={ctx.isDisabled ? 0.4 : 1}
      bg="transparent"
      _hover={{ bg: 'gray.50' }}
      onClick={ctx.decrement}
      disabled={ctx.isDisabled || ctx.isReadOnly}
      {...props}
    >
      <svg viewBox="0 0 24 24" width="0.6em" height="0.6em" fill="currentColor">
        <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" />
      </svg>
    </Box>
  );
});

NumberDecrementStepper.displayName = 'NumberDecrementStepper';

export default NumberInput;
